import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { PLATFORM, PLATFORM_ABBR } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

const NAV = [
    { label: "Services", id: "services", to: "/services" },
    { label: "Grants", id: "grants", to: "/grants" },
    { label: "Real estate", id: "realestate", to: "/services#realestate" },
    { label: "Marketplace", id: "marketplace", to: "/marketplace" },
    { label: "Automation", id: "automation", to: "/services#automation" },
    { label: "Shop", id: "shop", to: "/#shop" },
];

export function Header({ onSignIn, onConsult, dark = false }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const h = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", h);
        return () => window.removeEventListener("scroll", h);
    }, []);

    const go = (id) => {
        setOpen(false);
        const n = NAV.find((x) => x.id === id);
        const el = document.getElementById(id);
        if (el && (id === "shop" || id === "grants")) el.scrollIntoView({ behavior: "smooth" }); else navigate(n?.to || "/services");
    };
    const onDark = dark && !scrolled && !open;

    return (
        <header className={`si-header fixed top-0 inset-x-0 z-50 ${scrolled || open ? "is-scrolled" : "bg-transparent"}`} data-testid="site-header">
            <div className={`mx-auto flex max-w-7xl items-center justify-between px-5 transition-all duration-300 lg:px-8 ${scrolled ? "py-2.5" : "py-4 lg:py-5"}`}>
                <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="brand-logo">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-navy to-ocean font-display text-sm font-bold text-white shadow-md shadow-ocean/20 ring-1 ring-white/20">{PLATFORM_ABBR}</div>
                    <div className="hidden leading-tight text-left sm:block">
                        <div className={`whitespace-nowrap font-heading text-sm font-bold tracking-tight ${onDark ? "text-white" : "text-navy"}`}>Smarter Choice Investment Holdings</div>
                        <div className={`whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.14em] ${onDark ? "text-teal-light" : "text-teal"}`}>Powered by {PLATFORM}</div>
                    </div>
                </button>

                <nav className={`hidden shrink items-center gap-0.5 overflow-hidden rounded-full border px-1.5 py-1 backdrop-blur lg:flex ${onDark ? "border-white/15 bg-white/10" : "border-slate-200/70 bg-white/60"}`}>
                    {NAV.map((n) => (
                        <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}`}
                            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors ${onDark ? "text-slate-200 hover:bg-white/10 hover:text-white" : "text-slate-600 hover:bg-slate-100 hover:text-navy"}`}>{n.label}</button>
                    ))}
                </nav>

                <div className="hidden shrink-0 items-center gap-1.5 lg:flex">
                    {user ? (
                        <>
                            {user.role === "admin" && <Button variant="ghost" size="sm" onClick={() => navigate("/founder")} data-testid="header-founder-btn" className="text-teal hover:bg-teal/5">Chief of Staff</Button>}
                            {user.role === "admin" && <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="header-admin-btn" className="text-ocean hover:bg-ocean/5">Admin</Button>}
                            <Button variant="ghost" size="sm" onClick={() => navigate("/my-plan")} data-testid="header-myplan-btn" className="text-slate-700 hover:bg-slate-100">My Plan</Button>
                            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="header-dashboard-btn" className="text-slate-700 hover:bg-slate-100">Portal</Button>
                            <Button variant="outline" size="sm" onClick={logout} data-testid="header-logout-btn" className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-100">Sign out</Button>
                        </>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={onSignIn} data-testid="header-signin-btn" className={`rounded-full font-semibold ${onDark ? "text-white hover:bg-white/10 hover:text-white" : "text-navy hover:bg-slate-100"}`}>Sign in</Button>
                            {onConsult && <Button onClick={onConsult} data-testid="header-consult-btn" className={`rounded-full px-5 font-semibold shadow-md ${onDark ? "bg-teal text-white shadow-teal/30 hover:bg-teal-light" : "bg-navy text-white shadow-navy/20 hover:bg-navy-light"}`}>Book a Consultation</Button>}
                        </>
                    )}
                </div>

                <button className={`lg:hidden ${onDark ? "text-white" : "text-navy"}`} onClick={() => setOpen(!open)} data-testid="mobile-menu-btn"
                    aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="mobile-menu">{open ? <X /> : <Menu />}</button>
            </div>

            {open && (
                <div className="border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur lg:hidden" id="mobile-menu" data-testid="mobile-menu">
                    <div className="flex flex-col gap-1">
                        {NAV.map((n) => <button key={n.id} onClick={() => go(n.id)} className="rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-100">{n.label}</button>)}
                        <div className="mt-2 flex flex-col gap-2 border-t border-slate-200 pt-3">
                            {user ? (
                                <>
                                    <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} variant="outline" className="border-slate-300 text-slate-700">Portal</Button>
                                    <Button onClick={logout} variant="ghost" className="text-slate-600">Sign out</Button>
                                </>
                            ) : <>
                                <Button onClick={() => { setOpen(false); onSignIn(); }} variant="outline" className="border-slate-300 text-navy">Sign in</Button>
                                {onConsult && <Button onClick={() => { setOpen(false); onConsult(); }} data-testid="mobile-consult-btn" className="bg-teal text-white">Book a Consultation</Button>}
                            </>}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
